import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Pencil, Trash2, Send,
  Check, XCircle, Clock, FileDown, Copy, Info,
} from 'lucide-react';
import { useTranslation } from '../../i18n';

import { Card } from '../../components/Shared/Card';
import { Loading } from '../../components/Shared/Loading';
import { Button } from '../../components/Shared/Button';
import { Badge } from '../../components/Shared/Badge';
import { EmptyState } from '../../components/Shared/EmptyState';
import { SlideOver, SlideOverFooter } from '../../components/Shared/SlideOver';
import { Modal } from '../../components/Shared/Modal';
import { ConfirmDialog } from '../../components/Shared/ConfirmDialog';
import { FormField } from '../../components/Shared/FormField';
import { ContactPersonFields } from '../../components/Contacts/ContactPersonFields';
import { formatContactPerson, toSalutation, type Salutation } from '../../utils/salutation';
import { formatMoney } from '../../utils/money';
import { formatPercent } from '../../utils/percent';
import {
  SnippetTextArea, type OfferSnippetContext,
} from '../../components/Shared/SnippetTextArea';
import SearchSelect from '../../components/Shared/SearchSelect';

import { useOfferStore } from '../../store/useOfferStore';
import { PositionSlideOver } from './components/PositionSlideOver';
import { PositionsSection } from './components/PositionsSection';
import { useCustomerStore } from '../../store/useCustomerStore';
import { useObjectStore } from '../../store/useObjectStore';
import { useTextSnippetStore } from '../../store/useTextSnippetStore';
import { useToastStore } from '../../store/useToastStore';

import type {
  FacilityFlowCoreModulesOffersDtosOfferPositionDto,
} from '../../api/generated/models';

type PositionDto = FacilityFlowCoreModulesOffersDtosOfferPositionDto;

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '\u2014';
  const d = new Date(dateStr);
  return d.toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
};

function StatusBadge({ status, t }: {
  status?: string | null;
  t: (key: string) => string;
}) {
  const s = (status ?? '').toLowerCase();
  switch (s) {
    case 'draft':
      return <Badge variant="default">{t('offers:status.draft')}</Badge>;
    case 'sent':
      return <Badge variant="info">{t('offers:status.sent')}</Badge>;
    case 'accepted':
      return <Badge variant="success">{t('offers:status.accepted')}</Badge>;
    case 'rejected':
      return <Badge variant="danger">{t('offers:status.rejected')}</Badge>;
    case 'expired':
      return <Badge variant="warning">{t('offers:status.expired')}</Badge>;
    default:
      return <Badge variant="default">{status || '\u2014'}</Badge>;
  }
}

/* ------------------------------------------------------------------ */
/*  Edit Offer SlideOver                                              */
/* ------------------------------------------------------------------ */

interface EditOfferFormProps {
  isOpen: boolean;
  onClose: () => void;
  offer: NonNullable<ReturnType<typeof useOfferStore.getState>['selectedOffer']>;
  customerOptions: { id: string; label: string }[];
  objectOptions: { id: string; label: string }[];
  onCustomerChange: (id: string) => void;
  saving: boolean;
  onSave: (data: EditOfferFormData) => void;
  t: (key: string) => string;
}

interface EditOfferFormData {
  customerId: string;
  objectId: string;
  contactPersonName: string;
  contactSalutation: Salutation | null;
  offerDate: string;
  validUntilDate: string;
  taxRate: number;
  introductionText: string;
  closingText: string;
  paymentTerms: string;
}

function EditOfferSlideOver({
  isOpen, onClose, offer, customerOptions,
  objectOptions, onCustomerChange, saving, onSave, t,
}: EditOfferFormProps) {
  const [form, setForm] = useState<EditOfferFormData>({
    customerId: offer.customerId ?? '',
    objectId: offer.objectId ?? '',
    contactPersonName: offer.contactPersonName ?? '',
    contactSalutation: toSalutation(offer.contactSalutation),
    offerDate: offer.offerDate?.split('T')[0] ?? '',
    validUntilDate: offer.validUntilDate?.split('T')[0] ?? '',
    taxRate: offer.taxRate ?? 19,
    introductionText: offer.introductionText ?? '',
    closingText: offer.closingText ?? '',
    paymentTerms: offer.paymentTerms ?? '',
  });

  useEffect(() => {
    if (isOpen) {
      setForm({
        customerId: offer.customerId ?? '',
        objectId: offer.objectId ?? '',
        contactPersonName: offer.contactPersonName ?? '',
        contactSalutation: toSalutation(offer.contactSalutation),
        offerDate: offer.offerDate?.split('T')[0] ?? '',
        validUntilDate: offer.validUntilDate?.split('T')[0] ?? '',
        taxRate: offer.taxRate ?? 19,
        introductionText: offer.introductionText ?? '',
        closingText: offer.closingText ?? '',
        paymentTerms: offer.paymentTerms ?? '',
      });
    }
  }, [isOpen, offer]);

  const fetchSnippets = useTextSnippetStore((s) => s.fetchSnippets);
  useEffect(() => {
    if (isOpen) fetchSnippets({ isActive: true });
  }, [isOpen, fetchSnippets]);

  const objects = useObjectStore((s) => s.objects);
  // why: the object's responsible contact drives the auto-pick (FACF-102);
  // ContactPersonFields falls back to the customer's first contact without one.
  const preferredContactId = useMemo(
    () => objects.find((o) => o.id === form.objectId)?.responsibleContactId ?? null,
    [objects, form.objectId],
  );

  // why: unlike on create, the offer number is known here, so the preview resolves
  // {OfferNumber} exactly as the backend will on save (ADR-0020, Api repo).
  const renderContext: OfferSnippetContext = useMemo(
    () => ({
      customerId: form.customerId || null,
      objectId: form.objectId || null,
      contactSalutation: form.contactSalutation,
      contactPersonName: form.contactPersonName || null,
      offerNumber: offer.offerNumber ?? null,
      offerDate: form.offerDate || null,
      validUntilDate: form.validUntilDate || null,
      taxRate: form.taxRate,
    }),
    [
      form.customerId, form.objectId, form.contactSalutation, form.contactPersonName,
      offer.offerNumber, form.offerDate, form.validUntilDate, form.taxRate,
    ],
  );

  const handleCustomerChange = (id: string) => {
    setForm((f) => ({ ...f, customerId: id, objectId: '' }));
    onCustomerChange(id);
  };

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={t('offers:actions.edit')}
      size="xl"
      footer={
        <SlideOverFooter
          onCancel={onClose}
          onConfirm={() => onSave(form)}
          confirmText={t('common:actions.save')}
          loading={saving}
        />
      }
    >
      <div className="space-y-4">
        <SearchSelect
          label={t('offers:fields.customer')}
          options={customerOptions}
          value={form.customerId}
          onChange={handleCustomerChange}
          placeholder={t('offers:filter.allCustomers')}
        />
        <SearchSelect
          label={t('offers:fields.object')}
          options={objectOptions}
          value={form.objectId}
          onChange={(id) => setForm((f) => ({ ...f, objectId: id }))}
          placeholder={t('offers:fields.object')}
        />
        <ContactPersonFields
          customerId={form.customerId}
          salutation={form.contactSalutation}
          name={form.contactPersonName}
          onChange={({ salutation, name }) =>
            setForm((f) => ({ ...f, contactSalutation: salutation, contactPersonName: name }))
          }
          preferredContactId={preferredContactId}
          nameLabel={t('offers:fields.contactPerson')}
          namePlaceholder={t('offers:placeholders.contactPerson')}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            label={t('offers:fields.offerDate')}
            type="date"
            value={form.offerDate}
            onChange={(e) =>
              setForm((f) => ({ ...f, offerDate: e.target.value }))
            }
          />
          <FormField
            label={t('offers:fields.validUntilDate')}
            type="date"
            value={form.validUntilDate}
            onChange={(e) =>
              setForm((f) => ({ ...f, validUntilDate: e.target.value }))
            }
          />
        </div>
        <FormField
          label={t('offers:fields.taxRate')}
          type="number"
          value={String(form.taxRate)}
          onChange={(e) =>
            setForm((f) => ({
              ...f, taxRate: parseFloat(e.target.value) || 0,
            }))
          }
        />
        <SnippetTextArea
          label={t('offers:fields.introductionText')}
          name="introductionText"
          value={form.introductionText}
          onChange={(value) => setForm((f) => ({ ...f, introductionText: value }))}
          category="OfferIntroduction"
          documentType="offer"
          rows={3}
          placeholder={t('offers:placeholders.introductionText')}
          renderContext={renderContext}
        />
        <SnippetTextArea
          label={t('offers:fields.closingText')}
          name="closingText"
          value={form.closingText}
          onChange={(value) => setForm((f) => ({ ...f, closingText: value }))}
          category="OfferClosingText"
          documentType="offer"
          rows={3}
          placeholder={t('offers:placeholders.closingText')}
          renderContext={renderContext}
        />
        <SnippetTextArea
          label={t('offers:fields.paymentTerms')}
          name="paymentTerms"
          value={form.paymentTerms}
          onChange={(value) => setForm((f) => ({ ...f, paymentTerms: value }))}
          category="OfferPaymentTerms"
          documentType="offer"
          rows={2}
          placeholder={t('offers:placeholders.paymentTerms')}
          renderContext={renderContext}
        />
      </div>
    </SlideOver>
  );
}

/* ------------------------------------------------------------------ */
/*  Status Action Buttons                                             */
/* ------------------------------------------------------------------ */

function StatusActions({
  onEdit, onDelete, onStatusChange, onCreateFromOffer,
  isDraft, isSent, isAccepted, isReadOnly, t,
}: {
  onEdit: () => void;
  onDelete: () => void;
  onStatusChange: (target: string) => void;
  onCreateFromOffer: () => void;
  isDraft: boolean;
  isSent: boolean;
  isAccepted: boolean;
  /** Abgelehnt oder abgelaufen — endgültig, keine Aktionen. */
  isReadOnly: boolean;
  t: (key: string) => string;
}) {
  if (isReadOnly) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {isAccepted && (
        <>
          <Button
            onClick={onEdit}
            leftIcon={<Pencil className="w-4 h-4" strokeWidth={2} />}
          >
            {t('common:actions.edit')}
          </Button>
          <Button
            variant="secondary"
            onClick={onCreateFromOffer}
            leftIcon={<Copy className="w-4 h-4" strokeWidth={2} />}
          >
            {t('offers:actions.createFromOffer')}
          </Button>
        </>
      )}
      {isDraft && (
        <>
          <Button
            onClick={onEdit}
            leftIcon={<Pencil className="w-4 h-4" strokeWidth={2} />}
          >
            {t('common:actions.edit')}
          </Button>
          <Button
            variant="danger"
            onClick={onDelete}
            leftIcon={<Trash2 className="w-4 h-4" strokeWidth={2} />}
          >
            {t('offers:actions.delete')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => onStatusChange('sent')}
            leftIcon={<Send className="w-4 h-4" strokeWidth={2} />}
          >
            {t('offers:actions.markSent')}
          </Button>
        </>
      )}
      {isSent && (
        <>
          <Button
            onClick={() => onStatusChange('accepted')}
            leftIcon={<Check className="w-4 h-4" strokeWidth={2} />}
          >
            {t('offers:actions.markAccepted')}
          </Button>
          <Button
            variant="danger"
            onClick={() => onStatusChange('rejected')}
            leftIcon={<XCircle className="w-4 h-4" strokeWidth={2} />}
          >
            {t('offers:actions.markRejected')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => onStatusChange('expired')}
            leftIcon={<Clock className="w-4 h-4" strokeWidth={2} />}
          >
            {t('offers:actions.markExpired')}
          </Button>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Page Component                                               */
/* ------------------------------------------------------------------ */

export default function OfferDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['offers', 'invoices', 'common']);

  const {
    selectedOffer, loading, saving, getOfferById,
    updateOffer, deleteOffer, changeOfferStatus,
    deletePosition,
    reorderPositions, downloadPdf, downloadingPdf,
    createOfferFromOffer,
  } = useOfferStore();
  const { customers, fetchCustomers } = useCustomerStore();
  const { objects, fetchAllObjects, getObjectById, setActiveOffer } = useObjectStore();
  const { addToast } = useToastStore();
  const [isSetActiveConfirmOpen, setIsSetActiveConfirmOpen] = useState(false);
  const [objectActiveOfferId, setObjectActiveOfferId] = useState<string | null>(null);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState<PositionDto | null>(null);
  const [deletingPosition, setDeletingPosition] = useState<PositionDto | null>(null);
  const [isDeleteOfferOpen, setIsDeleteOfferOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState<string | null>(null);
  // FACF-92: Bearbeiten eines angenommenen Angebots fragt erst, ob angepasst oder
  // ein Nachfolger erstellt werden soll.
  const [isEditChoiceOpen, setIsEditChoiceOpen] = useState(false);

  useEffect(() => {
    if (id) {
      getOfferById(id);
    }
    fetchCustomers({ pageSize: 200 });
  }, [id, getOfferById, fetchCustomers]);

  useEffect(() => {
    if (selectedOffer?.customerId) {
      fetchAllObjects({ customerId: selectedOffer.customerId });
    }
  }, [selectedOffer?.customerId, fetchAllObjects]);

  useEffect(() => {
    let cancelled = false;
    if (selectedOffer?.objectId) {
      void (async () => {
        const obj = await getObjectById(selectedOffer.objectId as string);
        if (!cancelled) {
          setObjectActiveOfferId(
            (obj as { activeOfferId?: string | null } | null)?.activeOfferId ?? null,
          );
        }
      })();
    } else {
      setObjectActiveOfferId(null);
    }
    return () => {
      cancelled = true;
    };
  }, [selectedOffer?.objectId, getObjectById]);

  const status = (selectedOffer?.status ?? '').toLowerCase();
  const isDraft = status === 'draft';
  const isSent = status === 'sent';
  const isAccepted = status === 'accepted';
  const isReadOnly = ['rejected', 'expired'].includes(status);
  // Erst eine ausgestellte Rechnung friert das Angebot ein — dann bleibt nur der Weg
  // über ein Nachfolge-Angebot. Entwurfsrechnungen zählen nicht: das Annehmen erzeugt
  // selbst eine, die sonst jedes Angebot sofort sperren würde.
  const isLockedByInvoice = selectedOffer?.isLockedByInvoice ?? false;
  const isEditable = isDraft || (isAccepted && !isLockedByInvoice);

  const positions: PositionDto[] = [
    ...(selectedOffer?.positions ?? []),
  ].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

  const customerName = customers.find(
    (c) => c.id === selectedOffer?.customerId,
  )?.name ?? '\u2014';

  const objectName = objects.find(
    (o) => o.id === selectedOffer?.objectId,
  )?.name ?? '\u2014';

  const customerOptions = customers
    .filter((c) => c.id && c.name)
    .map((c) => ({ id: c.id!, label: c.name! }));

  const objectOptions = objects
    .filter((o) => o.id && o.name)
    .map((o) => ({ id: o.id!, label: o.name! }));

  const handleCustomerChangeInEdit = (customerId: string) => {
    fetchAllObjects({ customerId });
  };

  const handleSaveOffer = async (form: EditOfferFormData) => {
    if (!selectedOffer?.id) return;
    await updateOffer(selectedOffer.id, {
      customerId: form.customerId,
      objectId: form.objectId,
      // why: UpdateOfferRequest requires contactPersonName, introductionText and closingText
      // (non-nullable strings); only paymentTerms is optional.
      contactPersonName: form.contactPersonName || '',
      contactSalutation: form.contactSalutation,
      offerDate: form.offerDate,
      validUntilDate: form.validUntilDate,
      taxRate: form.taxRate,
      introductionText: form.introductionText || '',
      closingText: form.closingText || '',
      paymentTerms: form.paymentTerms || null,
    });
    setIsEditOpen(false);
    await getOfferById(selectedOffer.id);
  };

  const closePositionSlideOver = () => {
    const wasEditing = !!editingPosition;
    setIsPositionModalOpen(false);
    setEditingPosition(null);
    // why: schedule rules edited in the slide-over are saved directly on the task; reload so
    // billing months and interval text of the position reflect them.
    if (wasEditing && selectedOffer?.id) void getOfferById(selectedOffer.id);
  };

  const handleDeletePosition = async () => {
    if (!selectedOffer?.id || !deletingPosition?.id) return;
    await deletePosition(selectedOffer.id, deletingPosition.id);
    setDeletingPosition(null);
  };

  const handleDeleteOffer = async () => {
    if (!selectedOffer?.id) return;
    await deleteOffer(selectedOffer.id);
    navigate('/offers');
  };

  const handleStatusChange = async () => {
    if (!selectedOffer?.id || !statusTarget) return;
    const target = statusTarget;
    await changeOfferStatus(selectedOffer.id, target);
    setStatusTarget(null);
    if (target === 'accepted') {
      addToast(
        `${t('offers:messages.acceptedAndInvoiced')} — ${t('offers:messages.acceptedAndInvoicedHint')}`,
        'success',
        {
          duration: 8000,
          action: {
            label: t('offers:actions.openInvoiceList'),
            onClick: () => navigate('/invoices'),
          },
        },
      );
    }
  };

  const handleSetActiveOffer = async () => {
    if (!selectedOffer?.id || !selectedOffer.objectId) return;
    const ok = await setActiveOffer(selectedOffer.objectId, selectedOffer.id);
    setIsSetActiveConfirmOpen(false);
    if (ok) {
      setObjectActiveOfferId(selectedOffer.id);
    }
  };

  const handleCreateFromOffer = async () => {
    if (!selectedOffer?.id) return;
    setIsEditChoiceOpen(false);
    const newId = await createOfferFromOffer(selectedOffer.id);
    if (newId) navigate(`/offers/${newId}`);
  };

  const handleMovePosition = async (
    index: number, direction: 'up' | 'down',
  ) => {
    if (!selectedOffer?.positions || !selectedOffer.id) return;
    const sorted = [...selectedOffer.positions]
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= sorted.length) return;
    [sorted[index], sorted[targetIndex]] =
      [sorted[targetIndex], sorted[index]];
    const positionIds = sorted
      .map((p) => p.id)
      .filter(Boolean) as string[];
    await reorderPositions(selectedOffer.id, positionIds);
  };

  const statusConfirmVariant = (target: string | null) => {
    if (target === 'rejected' || target === 'expired') return 'warning';
    return 'info';
  };

  if (loading) return <Loading />;

  if (!selectedOffer) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate('/offers')}
          leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={2} />}
        >
          {t('offers:actions.back')}
        </Button>
        <Card>
          <EmptyState
            variant="error"
            title={t('offers:messages.loadError')}
            onRetry={() => id && getOfferById(id)}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => navigate('/offers')}
        leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={2} />}
      >
        {t('offers:actions.back')}
      </Button>

      {/* Header card */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900 font-mono-num truncate">
                {selectedOffer.offerNumber ?? t('offers:fields.offerNumber')}
              </h1>
              <StatusBadge status={selectedOffer.status} t={t} />
            </div>
          </div>
          <Button
            variant="secondary"
            onClick={() => selectedOffer?.id && downloadPdf(selectedOffer.id)}
            disabled={downloadingPdf || !selectedOffer?.id}
            leftIcon={<FileDown className="w-4 h-4" strokeWidth={2} />}
            className="w-full sm:w-auto"
          >
            {downloadingPdf
              ? t('offers:actions.generatingPdf')
              : t('offers:actions.downloadPdf')}
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <InfoItem
            label={t('offers:fields.customer')} value={customerName}
          />
          <InfoItem
            label={t('offers:fields.object')} value={objectName}
          />
          <InfoItem
            label={t('offers:fields.contactPerson')}
            value={formatContactPerson(
              toSalutation(selectedOffer.contactSalutation),
              selectedOffer.contactPersonName,
              t,
            )}
          />
          <InfoItem
            label={t('offers:fields.offerDate')}
            value={formatDate(selectedOffer.offerDate)}
            tabular
          />
          <InfoItem
            label={t('offers:fields.validUntilDate')}
            value={formatDate(selectedOffer.validUntilDate)}
            tabular
          />
        </div>

        <div className="mt-5 pt-4 border-t border-slate-100">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-3">
            {t('offers:detail.financials')}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <InfoItem
              label={t('offers:fields.totalNetAmount')}
              value={formatMoney(selectedOffer.totalNetAmount)}
              tabular
            />
            <InfoItem
              label={t('offers:fields.taxRate')}
              value={formatPercent(selectedOffer.taxRate)}
              tabular
            />
            <InfoItem
              label={t('offers:fields.totalGrossAmount')}
              value={formatMoney(selectedOffer.totalGrossAmount)}
              tabular
              emphasis
            />
          </div>
        </div>

        {(selectedOffer.introductionText
          || selectedOffer.closingText
          || selectedOffer.paymentTerms) && (
          <div className="mt-5 pt-4 border-t border-slate-100 space-y-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              {t('offers:detail.texts')}
            </h3>
            <TextBlock
              label={t('offers:fields.introductionText')}
              value={selectedOffer.introductionText}
              empty={t('offers:detail.noIntroduction')}
            />
            <TextBlock
              label={t('offers:fields.closingText')}
              value={selectedOffer.closingText}
              empty={t('offers:detail.noClosing')}
            />
            <TextBlock
              label={t('offers:fields.paymentTerms')}
              value={selectedOffer.paymentTerms}
              empty={t('offers:detail.noPaymentTerms')}
            />
          </div>
        )}
      </Card>

      {/* Action Buttons */}
      <StatusActions
        onEdit={() => {
          // Bei angenommenen Angeboten erst fragen, sonst direkt bearbeiten.
          if (isAccepted) setIsEditChoiceOpen(true);
          else setIsEditOpen(true);
        }}
        onDelete={() => setIsDeleteOfferOpen(true)}
        onStatusChange={(target) => setStatusTarget(target)}
        onCreateFromOffer={handleCreateFromOffer}
        isDraft={isDraft}
        isSent={isSent}
        isAccepted={isAccepted}
        isReadOnly={isReadOnly}
        t={t}
      />

      {status === 'accepted' && selectedOffer?.objectId && (
        <div className="flex justify-end items-center gap-3">
          {objectActiveOfferId === selectedOffer.id ? (
            <span className="text-[12px] text-slate-500 italic">
              {t('offers:hints.currentActiveOffer')}
            </span>
          ) : (
            <Button
              variant="secondary"
              onClick={() => setIsSetActiveConfirmOpen(true)}
              disabled={saving}
            >
              {t('offers:actions.setAsActiveOffer')}
            </Button>
          )}
        </div>
      )}

      {/* Positions Table */}
      <PositionsSection
        positions={positions}
        isEditable={isEditable}
        hasObject={!!selectedOffer.objectId}
        onAdd={() => {
          setEditingPosition(null);
          setIsPositionModalOpen(true);
        }}
        onEdit={(p) => {
          setEditingPosition(p);
          setIsPositionModalOpen(true);
        }}
        onDelete={(p) => setDeletingPosition(p)}
        onMove={handleMovePosition}
        t={t}
      />

      {/* Edit Offer SlideOver */}
      {isEditOpen && selectedOffer && (
        <EditOfferSlideOver
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          offer={selectedOffer}
          customerOptions={customerOptions}
          objectOptions={objectOptions}
          onCustomerChange={handleCustomerChangeInEdit}
          saving={saving}
          onSave={handleSaveOffer}
          t={t}
        />
      )}

      {/* Add/Edit Position Modal */}
      {selectedOffer.id && (
        <PositionSlideOver
          isOpen={isPositionModalOpen}
          onClose={closePositionSlideOver}
          offerId={selectedOffer.id}
          objectId={selectedOffer.objectId}
          position={editingPosition}
        />
      )}

      {/* Delete Offer ConfirmDialog */}
      <ConfirmDialog
        isOpen={isDeleteOfferOpen}
        onClose={() => setIsDeleteOfferOpen(false)}
        onConfirm={handleDeleteOffer}
        title={t('offers:actions.delete')}
        message={t('offers:confirmDelete', {
          offerNumber: selectedOffer?.offerNumber,
        })}
        variant="danger"
        loading={saving}
      />

      {/* Delete Position ConfirmDialog */}
      <ConfirmDialog
        isOpen={!!deletingPosition}
        onClose={() => setDeletingPosition(null)}
        onConfirm={handleDeletePosition}
        title={t('offers:positions.delete')}
        message={t('offers:positions.confirmDelete')}
        variant="danger"
        loading={saving}
      />

      {/* Status Change ConfirmDialog */}
      <ConfirmDialog
        isOpen={!!statusTarget}
        onClose={() => setStatusTarget(null)}
        onConfirm={handleStatusChange}
        title={t('offers:fields.status')}
        message={statusTarget
          ? t(`offers:confirmStatus.${statusTarget}`)
          : ''}
        variant={statusConfirmVariant(statusTarget)}
        loading={saving}
      />

      {/* Set Active Offer ConfirmDialog */}
      <ConfirmDialog
        isOpen={isSetActiveConfirmOpen}
        onClose={() => setIsSetActiveConfirmOpen(false)}
        onConfirm={handleSetActiveOffer}
        title={t('offers:confirm.setActive.title')}
        message={t('offers:confirm.setActive.message')}
        variant="warning"
        loading={saving}
      />

      {/* FACF-92: anpassen oder Nachfolge-Angebot */}
      <Modal
        isOpen={isEditChoiceOpen}
        onClose={() => setIsEditChoiceOpen(false)}
        title={t('offers:confirm.editOrNew.title')}
        size="md"
      >
        <div className="space-y-4">
          <p className="text-[13px] text-slate-700">
            {t('offers:confirm.editOrNew.message')}
          </p>

          {isLockedByInvoice && (
            <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <Info className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" strokeWidth={2} />
              <p className="text-[12px] text-slate-600">
                {t('offers:hints.lockedByInvoice')}
              </p>
            </div>
          )}

          <div className="grid gap-2">
            <Button
              onClick={() => {
                setIsEditChoiceOpen(false);
                setIsEditOpen(true);
              }}
              disabled={isLockedByInvoice || saving}
              leftIcon={<Pencil className="w-4 h-4" strokeWidth={2} />}
            >
              {t('offers:confirm.editOrNew.editExisting')}
            </Button>
            <Button
              variant="secondary"
              onClick={handleCreateFromOffer}
              disabled={saving}
              leftIcon={<Copy className="w-4 h-4" strokeWidth={2} />}
            >
              {t('offers:confirm.editOrNew.createNew')}
            </Button>
          </div>

          <div className="flex justify-end pt-1">
            <Button
              variant="ghost"
              onClick={() => setIsEditChoiceOpen(false)}
            >
              {t('common:actions.cancel')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Small presentational helpers                                      */
/* ------------------------------------------------------------------ */

function InfoItem({ label, value, tabular = false, emphasis = false }: {
  label: string;
  value?: string | null;
  tabular?: boolean;
  emphasis?: boolean;
}) {
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
        {label}
      </p>
      <p className={`mt-0.5 ${tabular ? 'tabular-nums' : ''} ${emphasis ? 'text-[15px] font-semibold text-[#00A871]' : 'text-[13px] font-medium text-slate-900'}`}>
        {value || '\u2014'}
      </p>
    </div>
  );
}

function TextBlock({ label, value, empty }: {
  label: string; value?: string | null; empty: string;
}) {
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">
        {label}
      </p>
      {value ? (
        <p className="text-[13px] text-slate-700 whitespace-pre-wrap">
          {value}
        </p>
      ) : (
        <p className="text-[12px] text-slate-500 italic">
          {empty}
        </p>
      )}
    </div>
  );
}
